<template>
    <div class="operations-list" :class="{ mobile: isMobile }">
        <div
        v-for="(operation, index) in parsedOperationsList"
        :key="index"
        class="operation"
        @click="rowClickHandler($event, operation)">
            <!-- *********************************************
            *                                                *
            *                    TOP CONTENT                 *
            *                                                *
            ********************************************** -->
            <div v-if="!isMobile" class="top-row">
                <div class="operation-description">{{ operation.typeDescription }}</div>
                <div class="operation-date">{{ getDate(operation.createDate) }}</div>
                <div
                class="operation-status"
                :class="operationsClassesHandler(operation.customerOperationStatus.id)">
                    <component
                    :is="statusSvgComponent(operationsIconHandler(operation.customerOperationStatus.id))"
                    class="cell-icon" />
                    <div class="status-description">{{operation.customerOperationStatus.description}}</div>
                </div>
            </div>
            <!-- *********************************************
            *                                                *
            *                  Center CONTENT                *
            *                                                *
            ********************************************** -->
            <div v-if="!isMobile" class="center-row">
                <div class="center-row-description">{{ operation.centerTextDescription }}</div>
            </div>
            <!-- *********************************************
            *                                                *
            *                   Bottom CONTENT               *
            *                                                *
            ********************************************** -->
            <div v-if="!isMobile" class="bottom-row" :class="{'two-columns': operation.specificTextBlocksData.length === 2}">
                <text-block
                v-for="(block, index) in operation.specificTextBlocksData"
                class="specific-text-block"
                :key="index"
                :params="{ label: block.label, content: block.content }"
                :mask="block.mask"
                applyEllipsis />
            </div>
            <!-- *********************************************
            *                                                *
            *                 Mobile only CONTENT            *
            *                                                *
            ********************************************** -->
            <div v-if="isMobile" class="mobile-content">
                <div class="operation-info">
                    <div class="top-description">{{ operation.typeDescription }}</div>
                    <div class="center-description">{{ operation.centerTextDescription }}</div>
                    <div class="bottom-text">
                        <div v-if="operation.mobileSpecificDescription !== '-'" class="description">
                            {{ operation.mobileSpecificDescription }}
                        </div>
                        <span v-if="operation.mobileSpecificDescription !== '-'">,</span>
                        <div class="date">{{ getDate(operation.createDate) }}</div>
                    </div>
                </div>
                <div
                class="operation-status"
                :class="operationsClassesHandler(operation.customerOperationStatus.id)">
                    <component
                    :is="statusSvgComponent(operationsIconHandler(operation.customerOperationStatus.id))"
                    class="cell-icon" />
                </div>
            </div>
        </div>
        <!-- Load More -->
		<load-more v-if="hasMore" :isLoadingMore="isLoadingMore" removeTableStyles @loadMore="emit('loadMore')"/>
    </div>
</template>

<script setup>
import TextBlock from '@/components/generic/textBlocks/Generic'
import LoadMore from '@/components/generic/table/LoadMore'

/* Vue */
import { computed, defineAsyncComponent } from 'vue'

/* Aux */
import { getSpecificTextBlocksData } from '@/assets/scripts/mappers/approvals'
import { amountFormat } from '@/assets/scripts/amounts'
import { getDate } from '@/assets/scripts/dateBuilders'
import { operationsIconHandler, operationsClassesHandler } from '@/assets/scripts/mappers/operationsHistory'
import { validateIfParentExists } from '@/assets/scripts/htmlManipulators'

/** Props
@operationsList {array} - array with operations
@hasMore {Boolean}
@loadMoreText {String} - Text for loadMore button
@loadingMoreText {String} - Text to display while loading more
@isLoadingMore {Boolean} - prop to tell table when to show loader or button
@isMobile {Boolean}
*/
const props = defineProps({
    operationsList: Array,
    hasMore: Boolean,
    loadMoreText: String,
    loadingMoreText: String,
    isLoadingMore: Boolean,
    isMobile: Boolean
})

const emit = defineEmits(['loadMore', 'rowClick'])

const parsedOperationsList = computed(() => {
    return props.operationsList.map(operation => ({
        ...operation,
        centerTextDescription: operation.amount.amount ? amountFormat(operation.amount.amount, operation.amount.currency) : operation.operatorName,
        specificTextBlocksData: getSpecificTextBlocksData(operation),
        mobileSpecificDescription: getSpecificTextBlocksData(operation).find(obj => obj.showInMobile).content
    }))
})

const statusSvgComponent = (icon) => {
    if (!icon) return null
	return defineAsyncComponent(() => import('@/assets/icons/' + icon + '.svg?inline'))
}

const rowClickHandler = (event, operation) => {
    if (validateIfParentExists(event.target, 'input-container')) return

    const dataToEmit = {
        ...operation,
        status: {
            id: operation.customerOperationStatus.id,
            classes: operationsClassesHandler(operation.customerOperationStatus.id),
            description: operation.customerOperationStatus.description,
            icon: operationsIconHandler(operation.customerOperationStatus.id)
        }
    }

    emit('rowClick', dataToEmit)
}
</script>

<style lang="scss" scoped>
.operations-list {
    width: 100%;

    .operation {
        display: flex;
        flex-direction: column;

        padding: 24px 19px 23px 16px;
        border: 1px solid var(--secondary-light-1);
        border-radius: 8px;

        cursor: pointer;

        &:not(:last-of-type) {
            margin-bottom: 16px;
        }

        @media (hover: hover) and (pointer: fine) {
            &:hover {
                background-color: var(--table-secondary-state-hover);
            }
        }

        .top-row {
            display: flex;
            align-items: center;

            margin-bottom: 6px;

            .operation-description,
            .operation-date {
                padding-right: 15px;
            }

            .operation-description {
                flex: 1 60%;

                @include H7-Regular;
                line-height: 16px;

                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;

                color: var(--secondary-dark-1);
            }

            .operation-date {
                flex: 1 15%;

                text-transform: capitalize;

                @include H6-Regular;
                line-height: 18px;

                color: var(--secondary-dark-2);
            }

            .operation-status {
                flex: 1 25%;

                @include H7-Regular;
                line-height: 16px;

                color: var(--secondary-dark-2);
            }
        }

        .center-row {
            margin-bottom: 16px;

            .center-row-description {
                @include H2-Regular;
                line-height: 36px;

                color: var(--primary);
            }
        }

        .bottom-row {
            display: flex;

            .specific-text-block {
                padding-right: 40px;

                &:last-of-type {
                    padding-right: 0;
                }
            }

            &.two-columns {
                .specific-text-block {
                    &:first-of-type {
                        width: calc((100% / 3)* 2);
                        flex-shrink: 0;
                    }
                }
            }
        }

        .top-row,
        .mobile-content {
            .operation-status {
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
                align-items: center;

                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;

                .status-description {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                :deep(svg) {
                    flex-shrink: 0;

                    padding-right: 8px;
                }

                &.negative {
                    :deep(.change-color) {
                        stroke: var(--negative);
                    }

                    :deep(.change-color-fill) {
                        fill: var(--negative);
                    }
                }

                &.warning {
                    :deep(.change-color) {
                        stroke: var(--warning);
                    }

                    :deep(.change-color-fill) {
                        fill: var(--warning);
                    }
                }

                &.positive {
                    :deep(.change-color) {
                        stroke: var(--positive);
                    }

                    :deep(.change-color-fill) {
                        fill: var(--positive);
                    }
                }
            }
        }
    }

    .load-more-wrapper {
        border-top: unset;
    }

    &.mobile {
        .operation {
            display: flex;
            justify-content: center;

            height: 88px;

            padding: 16px 24px 16px 16px;
            margin-bottom: 0;

            border: none;
            border-radius: 0%;
            border-bottom: 1px solid var(--secondary-light-2);
            .mobile-content {
                display: flex;
                justify-content: space-between;

                .operation-info {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;

                    width: calc(100% - 64px);

                    .top-description {
                        width: 100%;

                        @include H6-Medium;
                        line-height: 18px;
                        color: var(--secondary-dark-2);
                        text-transform: none;

                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }

                    .center-description {
                        @include H7-Regular;
                        line-height: 16px;

                        color: var(--secondary-dark-2);
                    }

                    .bottom-text {
                        display: flex;
                        flex-wrap: nowrap;

                        width: 100%;
                        margin-top: 8px;

                        @include H7-Regular;
                        line-height: 16px;
                        color: var(--secondary);

                        .description {
                            max-width: 60%;

                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                        }

                        span {
                            padding-right: 4px;
                        }

                        .date {
                            text-transform: capitalize;

                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                        }
                    }
                }

                .operation-status {
                    svg {
                        width: 24px;
                        height: 24px;
                    }
                }
            }
        }
    }
}
</style>
